/**
 * Polish language strings for the Vivatis PDB application.
 * All user-facing text should be defined here for easy localisation.
 */

const pl = {
  /* ------------------------------------------------------------------ */
  /* Global / shared                                                    */
  /* ------------------------------------------------------------------ */
  common: {
    products: 'Produkty',
    product: 'produkt',
    productsPlural: 'produkty',
    partner: 'Partner',
    name: 'Nazwa',
    category: 'Kategoria',
    yes: 'Tak',
    no: 'Nie',
    back: 'Wstecz',
    next: 'Dalej',
    skip: 'Pomiń',
    filter: 'Filtr',
    filterMode: 'Tryb filtrowania',
    and: 'I',
    or: 'LUB',
    clearAll: 'Wyczyść wszystko',
    removeAll: 'Usuń wszystko',
    browseProducts: 'Przeglądaj produkty',
    noProductsFound: 'Nie znaleziono produktów.',
    noResultsFor: (term: string) => `Brak wyników dla „${term}”.`,
    new: 'NOWOŚĆ',
    pdf: 'PDF',
    viewAll: 'Zobacz wszystkie',
    offlineBanner: 'Jesteś offline. Niektóre funkcje są ograniczone.',
    offlineTooltip: 'Niedostępne w trybie offline',
    offlineLanguageHint: 'Offline: język interfejsu się zmienia, dane produktów pozostają w wcześniej załadowanym języku do czasu ponownego połączenia z internetem',
    pwaUpdateAvailable: 'Dostępna jest nowa wersja. Aktualizacja...',
    pwaInstallTitle: 'Zainstaluj aplikację',
    pwaInstallDescription: 'Dodaj do ekranu głównego, aby mieć szybki dostęp.',
    pwaInstallButton: 'Zainstaluj',
  },

  /* ------------------------------------------------------------------ */
  /* Navigation                                                         */
  /* ------------------------------------------------------------------ */
  nav: {
    home: 'Strona główna',
    products: 'Produkty',
    wizard: 'Kreator',
    favorites: 'Ulubione',
    compare: 'Porównaj',
    cart: 'Dokumenty',
    searchPlaceholder: 'Szukaj produktów...',
    toggleTheme: 'Przełącz motyw',
    logout: 'Wyloguj',
    admin: 'Admin',
    toggleMenu: 'Przełącz menu',
  },

  /* ------------------------------------------------------------------ */
  /* Branding                                                           */
  /* ------------------------------------------------------------------ */
  brand: {
    vivatis: 'VIVATIS',
    pdb: 'PDB',
    pharma: 'PHARMA',
    productDatabase: 'Baza produktów',
    copyright: 'Vivatis Pharma GmbH © 2026',
  },

  /* ------------------------------------------------------------------ */
  /* Login                                                              */
  /* ------------------------------------------------------------------ */
  login: {
    email: 'E-mail',
    password: 'Hasło',
    showPassword: 'Pokaż hasło',
    hidePassword: 'Ukryj hasło',
    signingIn: 'Logowanie...',
    signIn: 'Zaloguj się',
    forgotPassword: 'Nie pamiętasz hasła?',
  },

  /* ------------------------------------------------------------------ */
  /* Home page                                                          */
  /* ------------------------------------------------------------------ */
  home: {
    welcomeBack: (name: string) => `Witaj ponownie${name ? `, ${name}` : ''}!`,
    whatToDo: 'Co chciałbyś dzisiaj zrobić?',
    stats: {
      products: 'Produkty',
      new: 'Nowe',
      favorites: 'Ulubione',
      inCart: 'W dokumencie',
    },
    actions: {
      browseProducts: 'Przeglądaj produkty',
      viewAllProducts: (count: number) => (count === 1 ? 'Zobacz 1 produkt' : `Zobacz wszystkie ${count} produkty`),
      browseProductsCount: (count: number) => `${count} produktów`,
      loading: 'Ładowanie...',
      startWizard: 'Uruchom kreator',
      findMatching: 'Znajdź pasujące produkty',
      favorites: 'Ulubione',
      productsSaved: (count: number) => `${count} zapisanych`,
      meetingCart: 'Dokument spotkania',
      productsInCart: (count: number) => `${count} w dokumencie`,
    },
    browseByIndication: 'Przeglądaj według wskazania',
    topIndications: 'Twoje najważniejsze wskazania',
    topIndicationsEmpty: 'Zacznij przeglądać produkty, aby zobaczyć tutaj swoje najczęściej oglądane wskazania.',
    newProducts: 'Nowe produkty',
    newProductsTooltip: 'Wszystkie produkty dodane w ciągu ostatnich 12 miesięcy są uważane za nowe.',
    newStudies: 'Nowe badania',
    newStudiesTooltip: 'Produkty z niedawno opublikowanymi badaniami klinicznymi w ciągu ostatnich 12 miesięcy.',
    newStudyBadge: 'Nowe badanie',
    publishedStudies: (count: number) => `${count} opublikowanych badań klinicznych`,
    favoritesTitle: 'Ulubione',
    noFavorites: 'Brak ulubionych. Kliknij ikonę serca na dowolnym produkcie, aby zapisać go tutaj.',
    productCountLabel: (count: number) => `${count} ${count === 1 ? 'produkt' : 'produktów'}`,
    recentlyViewed: 'Ostatnio oglądane',
    noRecentlyViewed: 'Brak ostatnio oglądanych produktów. Zacznij od',
    productCatalog: 'Katalogu produktów',
    viewAll: 'Zobacz wszystko',
  },

  /* ------------------------------------------------------------------ */
  /* Product list                                                       */
  /* ------------------------------------------------------------------ */
  productList: {
    sort: {
      relevance: 'Trafność',
      nameAsc: 'Nazwa (A-Z)',
      nameDesc: 'Nazwa (Z-A)',
      articleNumber: 'Nr artykułu',
      partner: 'Partner',
      indication: 'Wskazanie',
    },
    viewTable: 'Tabela',
    viewCards: 'Karty',
    productsFound: (count: number) => `Znaleziono ${count} produktów`,
    productCount: (count: number, total: number) => `${count} z ${total} produktów`,
    loading: 'Ładowanie produktów...',
    adjustFilters: 'Dostosuj filtry.',
    resetFilters: 'Zresetuj filtry',
  },

  /* ------------------------------------------------------------------ */
  /* Product search                                                     */
  /* ------------------------------------------------------------------ */
  productSearch: {
    placeholder: 'Szukaj według nazwy, numeru artykułu, składnika, wskazania...',
  },

  /* ------------------------------------------------------------------ */
  /* Product table                                                      */
  /* ------------------------------------------------------------------ */
  productTable: {
    articleNo: 'Nr artykułu',
    name: 'Nazwa',
    partner: 'Partner',
    indication: 'Wskazanie',
    application: 'Zastosowanie',
    country: 'Kraj',
    dailyDose: 'Dawka dzienna',
    claims: 'Oświadczenia',
    actions: 'Akcje',
  },

  /* ------------------------------------------------------------------ */
  /* Product detail                                                     */
  /* ------------------------------------------------------------------ */
  productDetail: {
    tabs: {
      overview: 'Przegląd',
      productData: 'Dane produktu',
      moreDetails: 'Cena',
    },
    notFound: 'Nie znaleziono produktu',
    backToOverview: 'Powrót do przeglądu',
    marketingClaims: 'Oświadczenia marketingowe',
    indications: 'Wskazania',
    applications: 'Zastosowania',
    countries: 'Kraje',
    description: 'Opis',
    shortDescription: 'Krótki opis',
    website: 'Strona produktu',
    multimedia: 'Strona multimedialna',
    studies: 'Badania',
    origin: 'Pochodzenie',
    dosage: 'Dawkowanie',
    appearance: 'Wygląd',
    category: 'Kategoria',
    articleNumber: 'Numer artykułu',
    active: 'Aktywny',
    created: 'Utworzono',
    showDailyPrice: 'Pokaż cenę dzienną',
    perDay: '/ dzień',
    generatingPdf: 'Generowanie PDF...',
    sendViaEmail: 'Wyślij e-mailem',
    emailSubject: (name: string, companyName: string) => `${companyName} — Karta produktu: ${name}`,
    emailBody: (name: string, userName: string, companyName: string) =>
      `Szanowny Kliencie,\n\nW załączeniu przesyłam kartę produktu dla ${name}.\n\nProszę o kontakt w przypadku pytań lub potrzeby dodatkowych informacji.\n\nZ poważaniem,\n${userName}\n${companyName}`,
    toastEmail: 'E-mail wysłany pomyślnie!',
  },

  /* ------------------------------------------------------------------ */
  /* Actions (favorite, cart, compare)                                   */
  /* ------------------------------------------------------------------ */
  actions: {
    addToFavorites: 'Dodaj do ulubionych',
    removeFromFavorites: 'Usuń z ulubionych',
    favorited: 'Dodano do ulubionych',
    favorite: 'Ulubione',
    addToCart: 'Dodaj do dokumentu',
    removeFromCart: 'Usuń z dokumentu',
    removeFromAllCarts: 'Usuń ze wszystkich dokumentów',
    inCart: 'W dokumencie',
    addToCartLabel: 'Dodaj do dokumentu',
    inCartLabel: 'W dokumencie',
    cartLabel: 'Dokumenty',
    addToComparison: 'Dodaj do porównania',
    removeFromComparison: 'Usuń z porównania',
    inComparison: 'W porównaniu',
    compared: 'Porównano',
    compare: 'Porównaj',
    manageFavorites: 'Zarządzaj ulubionymi',
    addToFavoritesNoGroup: 'Dodaj do ulubionych (bez grupy)',
    favoritesLabel: 'Ulubione',
  },

  /* ------------------------------------------------------------------ */
  /* Favorites page                                                     */
  /* ------------------------------------------------------------------ */
  favorites: {
    title: 'Ulubione',
    noFavorites: 'Brak ulubionych',
    noFavoritesDescription: 'Oznacz produkty jako ulubione, aby je tutaj zobaczyć.',
    deleteGroup: {
      title: 'Usuń grupę',
      message: (name: string) => `Czy na pewno chcesz usunąć „${name}"?`,
      moveLabel: 'Przenieś produkty do:',
      ungroupedOption: 'Nie w grupie (niezgrupowane)',
      confirm: 'Usuń grupę',
      cancel: 'Anuluj',
    },
    browseProducts: 'Przeglądaj produkty',
    dragProductsHint: 'Przeciągnij produkty tutaj',
    notInGroup: 'Bez grupy',
    notInGroupWithCount: (count: number) => `Bez grupy (${count})`,
    dragCopyHint: '⌘/⇧ wciśnięte — upuść, aby skopiować do grupy',
    dragMoveHint: 'Upuść, aby przenieść · przytrzymaj ⌘/⇧ aby skopiować',
    addGroupToDocument: 'Dodaj grupę do dokumentu',
    deleteGroupTitle: 'Usuń grupę',
    groupNamePlaceholder: 'Nazwa grupy...',
    create: 'Utwórz',
    cancel: 'Anuluj',
    newGroup: 'Nowa grupa',
    inDocument: 'W dokumencie',
    alreadyInDocument: 'Produkty są już w dokumencie.',
    addedToDocument: 'Produkty dodane do dokumentu.',
    removedFromDocument: 'Produkty usunięte z dokumentu.',
    addToDocument: 'Dodaj',
    productCountLabel: (count: number) => `${count} ${count === 1 ? 'produkt' : 'produktów'}`,
    openProductTitle: 'Otwórz',
  },

  /* ------------------------------------------------------------------ */
  /* Comparison page                                                    */
  /* ------------------------------------------------------------------ */
  comparison: {
    title: 'Porównanie produktów',
    loading: 'Ładowanie produktów...',
    productCount: (count: number, max: number) => `(${count}/${max})`,
    noProducts: 'Brak produktów do porównania',
    noProductsDescription: 'Dodaj produkty, aby porównać je obok siebie.',
    browseProducts: 'Przeglądaj produkty',
    addProduct: 'Dodaj produkt',
    removeFromComparison: 'Usuń z porównania',
    removeAll: 'Usuń wszystkie',
    showDifferencesOnly: 'Pokaż tylko różnice',
    showingDifferencesOnly: 'Wyświetlane tylko różnice',
    hideDailyPrice: 'Ukryj cenę dzienną',
    dailyPriceHidden: 'Cena dzienna ukryta',
    hidePrice: 'Ukryj cenę',
    priceHidden: 'Cena ukryta',
    allIdentical: 'Wszystkie wartości są identyczne dla tych produktów.',
    rows: {
      articleName: 'Nazwa artykułu',
      articleNumber: 'Numer artykułu',
      partner: 'Partner',
      manufacturer: 'Producent',
      category: 'Kategoria',
      clinicalStudies: 'Badania kliniczne',
      bioactiveIngredients: 'Składniki bioaktywne / Dawkowanie',
      indications: 'Wskazania',
      applications: 'Zastosowania',
      countries: 'Kraje',
      dosage: 'Dawkowanie',
      dailyDosage: 'Dawka dzienna',
      appearance: 'Wygląd',
      origin: 'Pochodzenie',
      sourceOrigin: 'Źródło / Pochodzenie',
      marketingClaims: 'Oświadczenia marketingowe',
      dailyPrice: 'Cena dzienna',
      price: 'Cena',
      novelFood: 'Novel Food',
    },
    novelFoodStatuses: {
      yes: 'Tak',
      ongoing: 'W toku',
      approved: 'Zatwierdzone',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Cart page                                                          */
  /* ------------------------------------------------------------------ */
  cart: {
    title: 'Dokument spotkania',
    subtitle: 'Produkty na spotkanie z klientem',
    emptyTitle: 'Dokument jest pusty',
    emptyDescription: 'Dodaj produkty z katalogu.',
    moveUp: 'Przesuń w górę',
    moveDown: 'Przesuń w dół',
    remove: 'Usuń',
    rename: 'Zmień nazwę',
    lockedNotice: (date: string) => `Ten dokument został wysłany ${date} i nie można go już zmienić.`,
    generatePdf: 'Generuj PDF',
    generatingPdf: 'Generowanie PDF...',
    sendViaEmail: 'Wyślij e-mailem',
    createOpportunity: 'Utwórz szansę sprzedaży w SalesForce',
    clearCart: 'Wyczyść dokument',
    archiveDocument: 'Archiwizuj dokument',
    confirmArchive: (name: string) => `Archiwizować „${name}"? Nie będzie już widoczny na liście dokumentów i będzie następnie dostępny tylko dla administratorów.`,
    deleteDocument: 'Usuń dokument',
    confirmClear: 'Czy na pewno chcesz wyczyścić dokument?',
    confirmDelete: (name: string) => `Usunąć „${name}"? Dokument i wszystkie jego produkty zostaną usunięte.`,
    toastPdf: 'PDF został wygenerowany i pobrany.',
    toastPdfError: 'Generowanie PDF nie powiodło się.',
    toastEmail: 'E-mail wysłany pomyślnie!',
    showDocuments: 'Pokaż dokumenty',
    newDocument: 'Nowy dokument',
    searchDocuments: 'Szukaj dokumentów...',
    noDocumentsFound: 'Nie znaleziono dokumentów.',
    items: 'elementy',
    comingSoon: 'Wkrótce dostępne',
    comingSoonDescription: 'Integracja z SalesForce jest obecnie w trakcie rozwoju. Ta funkcja będzie dostępna w przyszłej aktualizacji.',
    productCount: (count: number) => `${count} ${count === 1 ? 'produkt' : 'produktów'}`,
    emailSubject: (count: number, companyName: string) => `${companyName} — Wybór produktów (${count} produktów)`,
    emailBody: (productList: string, userName: string, companyName: string) => `Szanowny Kliencie,\n\nPoniżej znajdą Państwo wybór produktów z naszego ostatniego spotkania:\n\n${productList}\n\nProszę o kontakt w przypadku pytań lub potrzeby dodatkowych informacji na temat któregokolwiek z tych produktów.\n\nZ poważaniem,\n${userName}\n${companyName}`,
    email: {
      title: 'Wyślij e-mailem',
      recipients: 'Odbiorcy',
      subject: 'Temat',
      message: 'Wiadomość',
      placeholder: 'Wpisz adres e-mail...',
      cancel: 'Anuluj',
      send: 'Wyślij e-mail',
      sending: 'Wysyłanie...',
      invalidEmail: 'Proszę podać prawidłowy adres e-mail.',
      duplicateEmail: 'Ten adres e-mail został już dodany.',
      sendError: 'Nie udało się wysłać e-maila. Spróbuj ponownie.',
      products: 'Produkty',
      productsCount: (selected: number, total: number) => `${selected} z ${total}`,
      selectAll: 'Zaznacz wszystko',
      deselectAll: 'Odznacz wszystko',
      noProductsWarning: 'Wybierz co najmniej jeden produkt do dołączenia do e-maila.',
      fileTypesLegend: 'Dołącz dokumenty',
      fileTypesLoading: 'Wczytywanie dostępnych dokumentów…',
      fileTypes: {
        study: 'Badania',
        marketing: 'Dokumenty marketingowe',
        quality: 'Dokumenty jakościowe',
      },
      qualityHint: 'Dokumenty jakościowe nie są załączane. {email} wyśle je osobno do odbiorców.',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Settings                                                           */
  /* ------------------------------------------------------------------ */
  settings: {
    title: 'Ustawienia',
    subtitle: 'Skonfiguruj preferencje aplikacji',
    multiCart: {
      title: 'Wiele dokumentów',
      description:
        'Włącz wiele dokumentów spotkań, aby organizować produkty dla różnych klientów lub spotkań. Możesz mieć do 3 dokumentów i przełączać się między nimi z poziomu nagłówka.',
    },
    favoritesOnHome: {
      title: 'Ulubione na pulpicie',
      description: 'Wyświetl sekcję ulubionych na ekranie głównym, aby szybko uzyskać dostęp do zapisanych produktów.',
    },
    favoriteGroups: {
      title: 'Grupy ulubionych',
      description: 'Organizuj ulubione w grupy. Możesz utworzyć do 5 grup i przeciągać produkty między nimi. Każdą grupę można dodać do dokumentu jednym kliknięciem.',
    },
    productData: {
      title: 'Dane produktów',
      description: 'Pokazuje, kiedy dane produktów zostały ostatnio załadowane z serwera.',
      lastLoaded: 'Ostatnio załadowano',
      never: 'Jeszcze nigdy nie załadowano',
      reload: 'Załaduj produkty ponownie',
      reloading: 'Ponowne ładowanie...',
      reloadSuccess: 'Dane produktów załadowane ponownie',
      reloadError: 'Nie udało się ponownie załadować danych produktów',
    },
    changePassword: {
      title: 'Zmień hasło',
      description: 'Zaktualizuj hasło używane do logowania.',
      currentPassword: 'Obecne hasło',
      newPassword: 'Nowe hasło',
      confirmPassword: 'Potwierdź nowe hasło',
      submit: 'Zaktualizuj hasło',
      submitting: 'Aktualizowanie...',
      success: 'Twoje hasło zostało zaktualizowane.',
      passwordMismatch: 'Hasła nie są zgodne',
      error: 'Nie udało się zaktualizować hasła',
      backToSettings: 'Powrót do ustawień',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Wizard                                                             */
  /* ------------------------------------------------------------------ */
  wizard: {
    steps: {
      country: 'Kraj',
      application: 'Zastosowanie',
      indication: 'Wskazanie',
    },
    selectCountry: 'Wybierz kraj',
    selectCountrySubtitle: 'W jakich krajach produkt ma być dostępny?',
    selectApplication: 'Wybierz zastosowanie',
    selectApplicationSubtitle: 'Jaką postać dawkowania preferujesz?',
    selectIndication: 'Wybierz wskazanie',
    selectIndicationSubtitle: 'Które obszary zdrowotne są istotne?',
    showResults: 'Pokaż wyniki',
    noSelection: 'Nie dokonano wyboru',
    selected: (count: number) => `${count} wybranych`,
    filterAnd: 'I',
    filterOr: 'Lub',
    filterAndExplanation: 'Produkty muszą spełniać wszystkie wybrane kryteria',
    filterOrExplanation: 'Produkty muszą spełniać co najmniej jedno kryterium na krok',
  },

  /* ------------------------------------------------------------------ */
  /* Filter sidebar                                                     */
  /* ------------------------------------------------------------------ */
  filterSidebar: {
    title: 'Filtr',
    category: 'Kategoria',
    country: 'Kraj',
    application: 'Zastosowanie',
    indication: 'Wskazanie',
    partner: 'Partner',
    marketingClaims: 'Oświadczenia marketingowe',
    agreement: 'Umowa',
    agreements: {
      DA: 'DA — Umowa dystrybucyjna',
      CPA: 'CPA — Ochrona klienta',
      Gentleman: 'Gentleman Agreement',
      LOI: 'LOI — List intencyjny',
      none: 'Brak umowy',
    },
    agreementDescriptions: {
      DA: 'Umowa dystrybucyjna (wyłączna)',
      CPA: 'Umowa ochrony klienta (niewyłączna)',
      Gentleman: 'Gentleman Agreement (ustnie, bez umowy)',
      LOI: 'List intencyjny (wstępny etap DA)',
      none: 'Brak podpisanej umowy dla tego kraju',
    },
    newProducts: 'Nowe produkty',
    newStudy: 'Nowe badanie',
    restrictedTo: (territories: string) => `Twój widok jest ograniczony do ${territories}.`,
  },

  /* ------------------------------------------------------------------ */
  /* Marketing claim descriptions                                       */
  /* ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------ */
  /* Not Found (404)                                                    */
  /* ------------------------------------------------------------------ */
  notFound: {
    title: '404',
    heading: 'Strona nie została znaleziona',
    description: 'Strona, której szukasz, nie istnieje lub została przeniesiona.',
    goBack: 'Wróć',
    home: 'Strona główna',
  },

  /* ------------------------------------------------------------------ */
  /* Forgot Password                                                    */
  /* ------------------------------------------------------------------ */
  forgotPassword: {
    subtitle: 'Zresetuj swoje hasło',
    email: 'E-mail',
    emailPlaceholder: 'twoj@email.com',
    submit: 'Wyślij link resetujący',
    submitting: 'Wysyłanie...',
    success: 'Wysłaliśmy link do resetowania hasła na Twój adres e-mail.',
    backToLogin: 'Powrót do logowania',
    error: 'Nie udało się wysłać linku resetującego',
  },

  /* ------------------------------------------------------------------ */
  /* Reset Password                                                     */
  /* ------------------------------------------------------------------ */
  resetPassword: {
    subtitle: 'Ustaw nowe hasło',
    email: 'E-mail',
    newPassword: 'Nowe hasło',
    confirmPassword: 'Potwierdź hasło',
    submit: 'Zresetuj hasło',
    submitting: 'Resetowanie...',
    passwordMismatch: 'Hasła nie są zgodne',
    backToLogin: 'Powrót do logowania',
    error: 'Nie udało się zresetować hasła',
  },

  /* ------------------------------------------------------------------ */
  /* Marketing claim descriptions                                       */
  /* ------------------------------------------------------------------ */
  claimDescriptions: {
    Vegan: 'Nie zawiera składników ani produktów ubocznych pochodzenia zwierzęcego.',
    Organic: 'Certyfikowany ekologiczny — uprawiany bez syntetycznych pestycydów ani nawozów.',
    'Non-GMO': 'Wolny od organizmów genetycznie modyfikowanych.',
    'Clinically Studied': 'Skuteczność potwierdzona opublikowanymi badaniami klinicznymi.',
    Patented: 'Chroniony jednym lub więcej patentami na unikalną formułę lub proces.',
    Halal: 'Certyfikowany Halal — zgodny z islamskimi zasadami żywieniowymi.',
    Kosher: 'Certyfikowany Koszer — zgodny z żydowskimi prawami żywieniowymi.',
    'Gluten-Free': 'Nie zawiera glutenu — bezpieczny dla osób z celiakią lub nadwrażliwością na gluten.',
  },
  pdf: {
    productDataSheet: 'Karta produktu',
    productDataSheets: 'Karty produktow',
    keyFacts: 'Kluczowe dane',
    dailyDosage: 'Dawka dzienna',
    appearance: 'Postac',
    bioactiveIngredient: 'Skladnik bioaktywny',
    indications: 'Wskazania',
    possibleApplications: 'Mozliwe zastosowania',
    description: 'Opis',
    linkStudies: 'Badania',
    origin: 'Pochodzenie',
    confidential: 'Poufne -- Tylko do uzytku sluzbowego',
    productCountOne: ':count produkt',
    productCountOther: ':count produktow',
    clinicalStudies: 'Badania kliniczne',
    marketingClaims: 'Oswiadczenia marketingowe',
    territories: 'Terytoria',
    moq: 'Minimalna ilosc zamowienia w kg',
    shelfLife: 'Okres trwalosci',
    shelfLifeValue: ':months miesiecy',
    novelFoodStatus: 'Novel Food',
    badgeNew: 'NOWOSC',
    badgeNewStudy: 'NOWE BADANIE',
    contactPerson: 'Twoj kontakt w Vivatis:',
    website: 'Strona produktu',
    multimedia: 'Strona multimedialna',
  },
} as const;

export default pl;
